import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { blogPosts } from '../../../mocks/blogData';

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, [slug]);

  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#4A90E2] hover:text-[#2B5BA6] font-medium">
            Return to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPosts.filter(p => 
    p.slug !== slug && p.category === post.category
  ).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <article className="pt-20 sm:pt-24">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 py-12 sm:py-16">
          <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mb-4 sm:mb-6">
              <Link 
                to="/blog"
                className="inline-flex items-center text-sm text-gray-600 hover:text-[#4A90E2] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Blog
              </Link>
            </div>
            
            <div className="flex items-center space-x-4 mb-4 sm:mb-6">
              <span className="px-3 py-1 bg-[#4A90E2] text-white text-xs font-semibold rounded-full">
                {post.category}
              </span>
              <span className="text-sm text-gray-600">{post.readTime}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 font-poppins leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <div className="w-10 h-10 flex items-center justify-center bg-slate-200 rounded-full mr-3">
                  <i className="ri-user-line text-lg text-slate-600"></i>
                </div>
                <span className="font-medium">{post.author}</span>
              </div>
              <span>•</span>
              <time dateTime={post.date}>{post.date}</time>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl bg-slate-100">
            <img
              src={post.image}
              alt={post.title}
              loading="eager"
              className="w-full h-full object-cover object-center"
              style={{ imageRendering: 'auto' }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
          <div className="prose prose-lg max-w-none">
            {post.content.map((section, index) => (
              <div key={index} className="mb-8">
                {section.type === 'paragraph' && (
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {section.text}
                  </p>
                )}
                
                {section.type === 'heading' && (
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 mt-8">
                    {section.text}
                  </h2>
                )}
                
                {section.type === 'list' && (
                  <ul className="space-y-3 mb-6">
                    {section.items?.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                          <i className="ri-checkbox-circle-line text-xl text-[#4A90E2]"></i>
                        </div>
                        <span className="text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.type === 'quote' && (
                  <blockquote className="border-l-4 border-[#4A90E2] pl-6 py-4 my-8 bg-slate-50 rounded-r-lg">
                    <p className="text-lg text-gray-800 italic font-medium">
                      {section.text}
                    </p>
                  </blockquote>
                )}
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-12 p-6 sm:p-8 bg-gradient-to-br from-[#4A90E2]/10 to-[#2B5BA6]/10 rounded-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Ready for Your Roof Replacement?
            </h3>
            <p className="text-gray-700 mb-6">
              Get a virtual estimate in 24 hours with transparent pricing and guaranteed lowest rates.
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-[#2B5BA6] text-white font-semibold rounded-lg hover:bg-[#234a8a] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
              <i className="ri-arrow-right-line ml-2 text-lg"></i>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="bg-slate-50 py-12 sm:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {relatedPosts.map((relatedPost, index) => (
                  <Link
                    key={index}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                  >
                    <div className="w-full h-48 overflow-hidden bg-slate-100">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                        style={{ imageRendering: 'auto' }}
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-[#4A90E2]">
                          {relatedPost.category}
                        </span>
                        <span className="text-xs text-gray-500">{relatedPost.readTime}</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#4A90E2] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
